/**
 * ABB Weather Tile Layer Integration
 *
 * Fetches the layer catalog and per-layer metadata (including legends)
 * from the ABB Map Proxy-Cache at weather.gatehousemaritime.com.
 *
 * API docs: https://weather.gatehousemaritime.com/ABB/docs
 * Tiles: {base}/{layer}/{z}/{x}/{y}.webp?timeshift=N
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { STALE_TIMES } from '@ais-ui/custom-hooks';
import TileLayer from 'ol/layer/Tile';
import { XYZ } from 'ol/source';
import { useLayers } from '@ais-ui/react-openlayers';
import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useWeatherLegend } from '../../WeatherLegend';

// ==================== Types ====================

export interface ABBLayerInfo {
  href: string;
  title: string;
  label: string;
  group: string;
}

export interface ABBGroup {
  id: string;
  label: string;
}

export interface ABBColorTableElement {
  color: string;
  value: number;
  category: string;
}

export interface ABBLegend {
  label: string;
  unit: string;
  colorTableType: 'range' | 'discrete' | 'category';
  colorTable: ABBColorTableElement[];
}

export interface ABBTimepoint {
  timepoint: string;
  timestamp: number;
}

export interface ABBLayerMetadata {
  title: string;
  epsgs: string[];
  zoomlevels: number[];
  squaresize: number;
  timepoints: Record<string, ABBTimepoint>;
  timepointintervals: number;
  legends: ABBLegend[];
}

interface ABBCatalogResponse {
  title: string;
  description: string;
  responsible: string;
  docs: string;
  layers: { title: string; href: string; label: string; group: string }[];
}

// ==================== Constants ====================

const ABB_PROXY_BASE = 'https://weather.gatehousemaritime.com/ABB/v1';
const ABB_LAYER_ID_PREFIX = 'abb-weather-';

// ==================== API Client ====================

async function fetchABBCatalog(): Promise<ABBCatalogResponse> {
  const response = await fetch(`${ABB_PROXY_BASE}/`);
  if (!response.ok) {
    throw new Error(`ABB API Error: ${response.status}`);
  }
  return response.json();
}

async function fetchABBLayerMetadata(
  layerId: string,
): Promise<ABBLayerMetadata> {
  const response = await fetch(`${ABB_PROXY_BASE}/${layerId}`);
  if (!response.ok) {
    throw new Error(`ABB Layer Metadata Error: ${response.status}`);
  }
  return response.json();
}

// ==================== Hooks ====================

export function useABBCatalog() {
  return useQuery({
    queryKey: ['abb', 'catalog'],
    queryFn: fetchABBCatalog,
    staleTime: STALE_TIMES.MODERATE,
    retry: 1,
  });
}

/**
 * Fetch per-layer metadata (legends, timepoints, zoom levels).
 * Only fetches when a layerId is provided.
 */
export function useABBLayerMetadata(layerId: string | undefined) {
  return useQuery({
    queryKey: ['abb', 'layer-metadata', layerId],
    queryFn: () => fetchABBLayerMetadata(layerId!),
    enabled: !!layerId,
    staleTime: STALE_TIMES.MODERATE,
    retry: 1,
  });
}

/**
 * Returns ABB layers using server-provided labels and groups,
 * plus the unique groups for navigation.
 */
export function useABBLayers() {
  const { data: catalog, isLoading, error } = useABBCatalog();

  // Hide broken/unsupported groups
  const HIDDEN_GROUPS = ['aviation', 'map-features'];

  const layers: ABBLayerInfo[] = useMemo(() => {
    if (!catalog?.layers) return [];
    return catalog.layers
      .filter((raw) => !HIDDEN_GROUPS.includes(raw.group))
      .map((raw) => ({
        href: raw.href,
        title: raw.title,
        label: raw.label,
        group: raw.group,
      }));
  }, [catalog]);

  const groups: ABBGroup[] = useMemo(() => {
    const seen = new Map<string, string>();
    for (const layer of layers) {
      if (!seen.has(layer.group)) {
        seen.set(layer.group, layer.group);
      }
    }
    return Array.from(seen.keys()).map((id) => {
      // Use the group id as label, converting kebab-case to title case
      const label = id
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
      return { id, label };
    });
  }, [layers]);

  return { layers, groups, isLoading, error };
}

/**
 * Find the timeshift index whose timepoint is closest to the current time.
 *
 * ABB timepoints are a dictionary keyed by a zero-based index string
 * (e.g. "0", "1", "2" …). Each entry has an ISO `timepoint` string and a
 * Unix `timestamp` (seconds). The index is used as the `timeshift` query
 * parameter when requesting tiles:
 *
 *   /abb-proxy/{layer}/{z}/{x}/{y}.webp?timeshift={index}
 *
 * Different layers have different intervals (e.g. 5 min for radar,
 * 3 h for marine forecasts) and different start times, so we must
 * inspect the actual timepoints to pick the right one.
 *
 * Falls back to "0" if timepoints are empty or missing.
 */
export function findNearestTimeshift(
  timepoints: Record<string, ABBTimepoint> | undefined,
): string {
  if (!timepoints) return '0';

  const entries = Object.entries(timepoints);
  if (entries.length === 0) return '0';

  const nowSeconds = Math.floor(Date.now() / 1000);

  let bestKey = '0';
  let bestDiff = Number.POSITIVE_INFINITY;

  for (const [key, tp] of entries) {
    const diff = Math.abs(tp.timestamp - nowSeconds);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestKey = key;
    }
  }

  return bestKey;
}

/**
 * Build a tile URL template for an ABB layer.
 * Uses webp format for better compression.
 *
 * @param timeshift - Zero-based index into the layer's timepoints array.
 *   Use {@link findNearestTimeshift} to pick the timepoint closest to now.
 */
export function buildABBTileUrl(layerId: string, timeshift = '0'): string {
  return `${ABB_PROXY_BASE}/${layerId}/{z}/{x}/{y}.webp?timeshift=${timeshift}`;
}

export function getABBMapLayerId(layerId: string): string {
  return `${ABB_LAYER_ID_PREFIX}${layerId}`;
}

/**
 * Hook that provides toggle actions for ABB layers on the map.
 *
 * When adding a layer, we first fetch the layer metadata to discover
 * available timepoints, then select the timeshift closest to "now"
 * so the user sees current conditions rather than the oldest forecast.
 */
export function useABBLayerActions() {
  const { addLayer, removeLayer, getLayerById, doesLayerExist } = useLayers();
  const { toast } = useToast();
  const { messages } = useLocalization();
  const { dismissLegendForLayer, showLegend } = useWeatherLegend();
  const queryClient = useQueryClient();
  const [loadingLayerId, setLoadingLayerId] = useState<string | null>(null);

  const toggleLayer = useCallback(
    async (layer: ABBLayerInfo) => {
      const mapLayerId = getABBMapLayerId(layer.title);

      if (doesLayerExist(mapLayerId)) {
        const existing = getLayerById(mapLayerId);
        if (existing) {
          removeLayer(existing.layer);
          dismissLegendForLayer(layer.title);
          toast({
            title: messages.genericToastTitleSuccess,
            description: `Removed weather layer: ${layer.label}`,
          });
        }
        return;
      }

      setLoadingLayerId(layer.title);

      // Fetch metadata to determine the timepoint closest to now.
      // Uses React Query cache so repeated toggles don't re-fetch.
      // Falls back to timeshift=0 if metadata is unavailable.
      let timeshift = '0';
      let hasLegend = false;
      const isWindBarbLayer = /wind-symbol/i.test(layer.title);
      try {
        const metadata = await queryClient.fetchQuery({
          queryKey: ['abb', 'layer-metadata', layer.title],
          queryFn: () => fetchABBLayerMetadata(layer.title),
          staleTime: STALE_TIMES.MODERATE,
        });
        timeshift = findNearestTimeshift(metadata.timepoints);
        hasLegend = (metadata.legends?.length ?? 0) > 0 || isWindBarbLayer;
      } catch {
        // Metadata unavailable — use timeshift=0 (oldest available data)
        hasLegend = isWindBarbLayer;
      }

      const source = new XYZ({
        url: buildABBTileUrl(layer.title, timeshift),
        crossOrigin: 'anonymous',
        attributions: '',
      });
      const tileLayer = new TileLayer({ source, opacity: 0.7 });

      addLayer(
        tileLayer,
        layer.label,
        true,
        false,
        false,
        { type: 'abb-weather', abbLayerId: layer.title, hasLegend },
        mapLayerId,
        false,
        false,
        false,
        'above-ships',
      );

      // Auto-show legend when adding a weather layer (only if it has one)
      if (hasLegend) {
        showLegend(layer.title, layer.label);
      }

      setLoadingLayerId(null);

      toast({
        title: messages.genericToastTitleSuccess,
        description: `Added weather layer: ${layer.label}`,
      });
    },
    [
      addLayer,
      removeLayer,
      getLayerById,
      doesLayerExist,
      dismissLegendForLayer,
      showLegend,
      toast,
      messages,
      queryClient,
    ],
  );

  return { toggleLayer, loadingLayerId };
}
